/**
 * Clash Detection Manager
 * 
 * Real-time interference checking using Bounding Volume Hierarchy (BVH).
 * Supports hard clashes, soft clashes (clearance violations), and duplicates.
 */

import * as THREE from 'three';
import { v4 as uuidv4 } from 'uuid';

// ==================== Types ====================

export type ClashType = 'Hard' | 'Soft' | 'Duplicate' | 'Clearance';
export type ClashStatus = 'New' | 'Active' | 'Resolved' | 'Approved' | 'Reviewed';

export interface ClashResult {
    id: string;
    type: ClashType;
    status: ClashStatus;
    elementA: {
        id: string;
        category: string;
        type: string;
    };
    elementB: {
        id: string;
        category: string;
        type: string;
    };
    point: THREE.Vector3;
    distance: number;  // Negative = overlap, Positive = clearance
    createdAt: number;
    resolvedAt?: number;
}

export interface ClashTest {
    id: string;
    name: string;
    selectionA: ClashSelection;
    selectionB: ClashSelection;
    tolerance: number;
    includeLinked: boolean;
    lastRun?: number;
    resultCount?: number;
}

export interface ClashSelection {
    categories?: string[];
    elementIds?: string[];
    excludeIds?: string[];
    includeTypes?: string[];
}

export interface ClashReport {
    testId: string;
    testName: string;
    runTime: number;
    totalClashes: number;
    newClashes: number;
    resolvedClashes: number;
    clashMatrix: Map<string, Map<string, number>>;  // Category A -> Category B -> Count
    clashes: ClashResult[];
}

// ==================== AABB (Axis-Aligned Bounding Box) ====================

interface AABB {
    min: THREE.Vector3;
    max: THREE.Vector3;
    elementId: string;
    category: string;
    type: string;
}

// ==================== BVH Node ====================

interface BVHNode {
    bounds: THREE.Box3;
    left?: BVHNode;
    right?: BVHNode;
    elements?: AABB[];
}

// ==================== Clash Detection Manager ====================

export class ClashDetectionManager {
    private elements: Map<string, AABB> = new Map();
    private bvhRoot: BVHNode | null = null;
    private clashTests: Map<string, ClashTest> = new Map();
    private clashResults: Map<string, ClashResult> = new Map();
    private previousClashIds: Set<string> = new Set();

    // ==================== Element Registration ====================

    public registerElement(
        id: string,
        category: string,
        type: string,
        bounds: THREE.Box3
    ): void {
        const aabb: AABB = {
            min: bounds.min.clone(),
            max: bounds.max.clone(),
            elementId: id,
            category,
            type
        };
        this.elements.set(id, aabb);
        this.invalidateBVH();
    }

    public registerFromBIMElement(element: any): void {
        if (!element.id || !element.category) return;

        let bounds: THREE.Box3 | null = null;

        if (element.geometryData) {
            const gd = element.geometryData;

            if (gd.start && gd.end) {
                // Linear element
                const start = new THREE.Vector3(...gd.start);
                const end = new THREE.Vector3(...gd.end);
                bounds = new THREE.Box3().setFromPoints([start, end]);
                
                // Expand for width/height
                const width = (gd.width || gd.diameter || 200) / 1000;
                bounds.expandByScalar(width / 2);
            } else if (gd.boundary) {
                // Surface element
                const points = gd.boundary.map((p: number[]) => 
                    new THREE.Vector3(p[0], p[1], 0)
                );
                bounds = new THREE.Box3().setFromPoints(points);
                bounds.expandByScalar(gd.thickness || 0.2);
            } else if (gd.position) {
                // Point-based element
                const pos = new THREE.Vector3(...gd.position);
                bounds = new THREE.Box3().setFromCenterAndSize(
                    pos,
                    new THREE.Vector3(0.5, 0.5, 0.5)
                );
            }
        }

        if (bounds) {
            this.registerElement(element.id, element.category, element.type, bounds);
        }
    }

    public unregisterElement(id: string): void {
        this.elements.delete(id);
        this.invalidateBVH();
    }

    public updateElement(id: string, newBounds: THREE.Box3): void {
        const existing = this.elements.get(id);
        if (existing) {
            existing.min = newBounds.min.clone();
            existing.max = newBounds.max.clone();
            this.invalidateBVH();
        }
    }

    // ==================== BVH Construction ====================

    private invalidateBVH(): void {
        this.bvhRoot = null;
    }

    private buildBVH(): void {
        const elements = Array.from(this.elements.values());
        if (elements.length === 0) {
            this.bvhRoot = null;
            return;
        }
        this.bvhRoot = this.buildBVHNode(elements);
    }

    private buildBVHNode(elements: AABB[]): BVHNode {
        // Leaf node
        if (elements.length <= 4) {
            const bounds = new THREE.Box3();
            for (const el of elements) {
                bounds.expandByPoint(el.min);
                bounds.expandByPoint(el.max);
            }
            return { bounds, elements };
        }

        // Calculate bounds
        const bounds = new THREE.Box3();
        const centers: THREE.Vector3[] = [];
        for (const el of elements) {
            bounds.expandByPoint(el.min);
            bounds.expandByPoint(el.max);
            centers.push(new THREE.Vector3().addVectors(el.min, el.max).multiplyScalar(0.5));
        }

        // Find longest axis
        const size = new THREE.Vector3();
        bounds.getSize(size);
        const axis = size.x > size.y ? (size.x > size.z ? 'x' : 'z') : (size.y > size.z ? 'y' : 'z');

        // Sort by axis
        const sorted = elements.slice().sort((a, b) => {
            const getAxisValue = (vec: THREE.Vector3, ax: string): number => {
                if (ax === 'x') return vec.x;
                if (ax === 'y') return vec.y;
                return vec.z;
            };
            const centerA = (getAxisValue(a.min, axis) + getAxisValue(a.max, axis)) / 2;
            const centerB = (getAxisValue(b.min, axis) + getAxisValue(b.max, axis)) / 2;
            return centerA - centerB;
        });

        // Split
        const mid = Math.floor(sorted.length / 2);
        const left = this.buildBVHNode(sorted.slice(0, mid));
        const right = this.buildBVHNode(sorted.slice(mid));

        return { bounds, left, right };
    }

    // ==================== Clash Test Management ====================

    public createClashTest(
        name: string,
        selectionA: ClashSelection,
        selectionB: ClashSelection,
        tolerance: number = 0
    ): ClashTest {
        const test: ClashTest = {
            id: uuidv4(),
            name,
            selectionA,
            selectionB,
            tolerance,
            includeLinked: true
        };
        this.clashTests.set(test.id, test);
        return test;
    }

    public getClashTest(id: string): ClashTest | undefined {
        return this.clashTests.get(id);
    }

    public getAllClashTests(): ClashTest[] {
        return Array.from(this.clashTests.values());
    }

    // ==================== Clash Detection ====================

    public runClashTest(testId: string): ClashReport {
        const test = this.clashTests.get(testId);
        if (!test) {
            return this.createEmptyReport(testId, 'Test not found');
        }

        const startTime = performance.now();

        // Ensure BVH is built
        if (!this.bvhRoot) {
            this.buildBVH();
        }

        // Get selections
        const setA = this.getElementsBySelection(test.selectionA);
        const setB = this.getElementsBySelection(test.selectionB);

        // Store previous clashes for comparison
        this.previousClashIds = new Set(
            Array.from(this.clashResults.values())
                .filter(c => c.status !== 'Resolved')
                .map(c => this.getClashKey(c.elementA.id, c.elementB.id))
        );

        const newClashes: ClashResult[] = [];
        const clashMatrix = new Map<string, Map<string, number>>();
        let resolvedCount = 0;

        // Check all pairs
        for (const elemA of setA) {
            for (const elemB of setB) {
                // Skip self-intersection
                if (elemA.elementId === elemB.elementId) continue;

                // Quick AABB test
                const boundsA = new THREE.Box3(elemA.min, elemA.max);
                const boundsB = new THREE.Box3(elemB.min, elemB.max);
                
                // Expand by tolerance
                if (test.tolerance > 0) {
                    boundsA.expandByScalar(test.tolerance);
                }

                if (boundsA.intersectsBox(boundsB)) {
                    const clash = this.createClashResult(elemA, elemB, boundsA, boundsB);
                    
                    // Check if this is a new clash
                    const clashKey = this.getClashKey(elemA.elementId, elemB.elementId);
                    if (!this.previousClashIds.has(clashKey)) {
                        clash.status = 'New';
                        newClashes.push(clash);
                    }

                    this.clashResults.set(clash.id, clash);

                    // Update matrix
                    if (!clashMatrix.has(elemA.category)) {
                        clashMatrix.set(elemA.category, new Map());
                    }
                    const catMap = clashMatrix.get(elemA.category)!;
                    catMap.set(elemB.category, (catMap.get(elemB.category) || 0) + 1);
                }
            }
        }

        // Check for resolved clashes
        for (const key of this.previousClashIds) {
            const stillExists = Array.from(this.clashResults.values())
                .some(c => this.getClashKey(c.elementA.id, c.elementB.id) === key);
            if (!stillExists) {
                resolvedCount++;
            }
        }

        const endTime = performance.now();

        test.lastRun = Date.now();
        test.resultCount = this.clashResults.size;

        return {
            testId,
            testName: test.name,
            runTime: endTime - startTime,
            totalClashes: this.clashResults.size,
            newClashes: newClashes.length,
            resolvedClashes: resolvedCount,
            clashMatrix,
            clashes: Array.from(this.clashResults.values())
        };
    }

    private getElementsBySelection(selection: ClashSelection): AABB[] {
        let elements = Array.from(this.elements.values());

        if (selection.categories && selection.categories.length > 0) {
            elements = elements.filter(e => selection.categories!.includes(e.category));
        }

        if (selection.elementIds && selection.elementIds.length > 0) {
            elements = elements.filter(e => selection.elementIds!.includes(e.elementId));
        }

        if (selection.excludeIds && selection.excludeIds.length > 0) {
            elements = elements.filter(e => !selection.excludeIds!.includes(e.elementId));
        }

        return elements;
    }

    private createClashResult(elemA: AABB, elemB: AABB, boundsA: THREE.Box3, boundsB: THREE.Box3): ClashResult {
        // Find intersection point
        const intersection = boundsA.clone().intersect(boundsB);
        const point = new THREE.Vector3();
        intersection.getCenter(point);

        // Calculate penetration distance
        const sizeA = new THREE.Vector3();
        const sizeB = new THREE.Vector3();
        boundsA.getSize(sizeA);
        boundsB.getSize(sizeB);
        
        const centerA = new THREE.Vector3();
        const centerB = new THREE.Vector3();
        boundsA.getCenter(centerA);
        boundsB.getCenter(centerB);
        
        const distance = centerA.distanceTo(centerB) - (sizeA.length() + sizeB.length()) / 4;

        return {
            id: uuidv4(),
            type: distance < -0.01 ? 'Hard' : 'Soft',
            status: 'Active',
            elementA: {
                id: elemA.elementId,
                category: elemA.category,
                type: elemA.type
            },
            elementB: {
                id: elemB.elementId,
                category: elemB.category,
                type: elemB.type
            },
            point,
            distance,
            createdAt: Date.now()
        };
    }

    private getClashKey(idA: string, idB: string): string {
        return idA < idB ? `${idA}-${idB}` : `${idB}-${idA}`;
    }

    private createEmptyReport(testId: string, error: string): ClashReport {
        return {
            testId,
            testName: error,
            runTime: 0,
            totalClashes: 0,
            newClashes: 0,
            resolvedClashes: 0,
            clashMatrix: new Map(),
            clashes: []
        };
    }

    // ==================== Clash Status Management ====================

    public updateClashStatus(clashId: string, status: ClashStatus): void {
        const clash = this.clashResults.get(clashId);
        if (clash) {
            clash.status = status;
            if (status === 'Resolved') {
                clash.resolvedAt = Date.now();
            }
        }
    }

    public getClash(id: string): ClashResult | undefined {
        return this.clashResults.get(id);
    }

    public getAllClashes(): ClashResult[] {
        return Array.from(this.clashResults.values());
    }

    public getClashesByStatus(status: ClashStatus): ClashResult[] {
        return Array.from(this.clashResults.values()).filter(c => c.status === status);
    }

    public clearResolvedClashes(): void {
        for (const [id, clash] of this.clashResults) {
            if (clash.status === 'Resolved') {
                this.clashResults.delete(id);
            }
        }
    }

    // ==================== Statistics ====================

    public getStatistics(): {
        totalElements: number;
        totalClashes: number;
        byType: Record<ClashType, number>;
        byStatus: Record<ClashStatus, number>;
    } {
        const byType: Record<ClashType, number> = { Hard: 0, Soft: 0, Duplicate: 0, Clearance: 0 };
        const byStatus: Record<ClashStatus, number> = { New: 0, Active: 0, Resolved: 0, Approved: 0, Reviewed: 0 };

        for (const clash of this.clashResults.values()) {
            byType[clash.type]++;
            byStatus[clash.status]++;
        }

        return {
            totalElements: this.elements.size,
            totalClashes: this.clashResults.size,
            byType,
            byStatus
        };
    }
}
